import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, Download, FolderOpen, Plus,
  ChevronRight, Settings, FileStack, X, ArrowLeft, Loader2
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/spec-builder`;

export default function TechSpecBuilder({ user }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("build");
  const [sections, setSections] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newSectionName, setNewSectionName] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadLabels, setUploadLabels] = useState({});
  const [selectedSection, setSelectedSection] = useState("default");
  const [deleteItems, setDeleteItems] = useState(new Set());
  const [uploading, setUploading] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);
  const [itemOrder, setItemOrder] = useState({});
  const [outputName, setOutputName] = useState("specification");
  const [generating, setGenerating] = useState(false);

  const fileInputRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [sectionsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/sections`),
        axios.get(`${API}/items`)
      ]);
      setSections(sectionsRes.data);
      setItems(itemsRes.data);
      if (sectionsRes.data.length > 0 && !sectionsRes.data.find(s => s.id === selectedSection)) {
        setSelectedSection(sectionsRes.data[0].id);
      }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [selectedSection]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;
    try {
      await axios.post(`${API}/sections`, { name: newSectionName.trim() });
      setNewSectionName("");
      toast.success("Section added");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add section");
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      await axios.delete(`${API}/sections/${sectionId}`);
      toast.success("Section deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const pptxFiles = files.filter(f => f.name.endsWith('.pptx'));
    const newLabels = { ...uploadLabels };
    pptxFiles.forEach(f => { if (!newLabels[f.name]) newLabels[f.name] = f.name.replace('.pptx', ''); });
    setUploadFiles(prev => [...prev, ...pptxFiles]);
    setUploadLabels(newLabels);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (fileName) => {
    setUploadFiles(uploadFiles.filter(f => f.name !== fileName));
    const newLabels = { ...uploadLabels };
    delete newLabels[fileName];
    setUploadLabels(newLabels);
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('label', uploadLabels[file.name] || file.name);
        fd.append('section_id', selectedSection);
        await axios.post(`${API}/items`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`${uploadFiles.length} file(s) uploaded`);
      setUploadFiles([]);
      setUploadLabels({});
      fetchData();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (deleteItems.size === 0) return;
    try {
      await axios.delete(`${API}/items`, { data: Array.from(deleteItems) });
      setDeleteItems(new Set());
      toast.success("Items deleted");
      fetchData();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const toggleDeleteItem = (itemId) => {
    const n = new Set(deleteItems);
    n.has(itemId) ? n.delete(itemId) : n.add(itemId);
    setDeleteItems(n);
  };

  const toggleItemSelection = (item) => {
    if (selectedItems.find(i => i.id === item.id)) {
      setSelectedItems(selectedItems.filter(i => i.id !== item.id));
      const o = { ...itemOrder }; delete o[item.id]; setItemOrder(o);
    } else {
      setSelectedItems([...selectedItems, item]);
      setItemOrder({ ...itemOrder, [item.id]: selectedItems.length + 1 });
    }
  };

  const getOrderedItems = () => [...selectedItems].sort((a, b) => (itemOrder[a.id] || 0) - (itemOrder[b.id] || 0));

  const handleGenerateSpec = async () => {
    if (selectedItems.length === 0) { toast.error("Select at least one item"); return; }
    setGenerating(true);
    try {
      const orderedIds = getOrderedItems().map(i => i.id);
      const resp = await axios.post(`${API}/merge`, { item_ids: orderedIds, output_name: outputName || "specification" }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${outputName || 'specification'}.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Specification generated!");
    } catch (err) {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const getItemsBySection = (sectionId) => items.filter(i => i.section_id === sectionId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d1520" }}>
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0d1520" }}>
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-30" style={{ background: "#0d1520" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-gray-400 hover:text-white transition-colors" data-testid="sb-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <FileStack className="w-5 h-5 text-violet-400" />
              <h1 className="text-lg font-mono font-semibold text-white">Technical Spec Builder</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />}
            <span className="text-xs text-gray-400 hidden sm:inline">{user?.name || user?.email}</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-[#0a0a0c]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1">
          {[
            { id: "build", label: "Build Spec", icon: FileStack },
            { id: "admin", label: "Admin", icon: Settings }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`sb-tab-${tab.id}`}
              className={`px-5 py-3 font-mono text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 transition-colors
                ${activeTab === tab.id ? "border-violet-500 text-violet-400" : "border-transparent text-gray-500 hover:text-gray-300"}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* BUILD TAB */}
        {activeTab === "build" && (
          <div className="space-y-5" data-testid="sb-build-section">
            {items.length === 0 ? (
              <div className="bg-white/[0.03] border border-white/10 p-12 text-center">
                <FileText className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 font-mono text-sm">No items in library. Go to Admin to upload PPTX files.</p>
              </div>
            ) : (
              <div className="grid lg:grid-cols-5 gap-6">
                {/* Selection */}
                <div className="lg:col-span-3 space-y-4">
                  {sections.map(section => {
                    const sItems = getItemsBySection(section.id);
                    if (sItems.length === 0) return null;
                    return (
                      <div key={section.id} className="bg-white/[0.03] border border-white/10 p-5" data-testid={`sb-section-${section.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-mono text-gray-200 flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-violet-400" /> {section.name}
                          </h3>
                          <span className="text-xs text-gray-600 font-mono">{sItems.length} item(s)</span>
                        </div>
                        <div className="space-y-1.5">
                          {sItems.map(item => {
                            const selected = !!selectedItems.find(i => i.id === item.id);
                            return (
                              <div key={item.id}
                                className={`flex items-center gap-3 px-3 py-2.5 border transition-all cursor-pointer
                                  ${selected ? "border-violet-500/50 bg-violet-500/10" : "border-white/5 hover:border-white/10"}`}
                                onClick={() => toggleItemSelection(item)}
                                data-testid={`sb-item-${item.id}`}
                              >
                                <input type="checkbox" checked={selected} readOnly className="accent-violet-500 pointer-events-none" />
                                <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-mono text-gray-200 truncate">{item.label}</div>
                                  <div className="text-xs text-gray-600 truncate">{item.original_name}</div>
                                </div>
                                {selected && (
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <span className="text-xs text-gray-500 font-mono">Order:</span>
                                    <input type="number" min="1"
                                      value={itemOrder[item.id] || 1}
                                      onChange={(e) => { e.stopPropagation(); setItemOrder({ ...itemOrder, [item.id]: parseInt(e.target.value) || 1 }); }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-12 bg-white/5 border border-white/10 text-center text-xs font-mono text-gray-200 py-1
                                        focus:outline-none focus:border-violet-500/50"
                                      data-testid={`sb-order-${item.id}`}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary & Generate */}
                <div className="lg:col-span-2">
                  <div className="sticky top-16 space-y-4">
                    <div className="bg-white/[0.03] border border-white/10 p-5">
                      <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                        <ChevronRight className="w-4 h-4 text-violet-400" />
                        Selection ({selectedItems.length})
                      </h3>
                      {selectedItems.length === 0 ? (
                        <p className="text-xs text-gray-600 font-mono">Select items from the left to build your spec.</p>
                      ) : (
                        <div className="space-y-1.5 mb-4" data-testid="sb-selection-list">
                          {getOrderedItems().map((item, idx) => (
                            <div key={item.id} className="flex items-center gap-2 text-xs font-mono">
                              <span className="w-5 h-5 flex items-center justify-center bg-violet-500/20 text-violet-400 text-[10px] font-bold">
                                {idx + 1}
                              </span>
                              <span className="text-gray-300 truncate">{item.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mb-3">
                        <label className="text-xs text-gray-500 font-mono block mb-1">Output filename</label>
                        <input type="text" value={outputName}
                          onChange={(e) => setOutputName(e.target.value)}
                          placeholder="specification"
                          className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200
                            focus:outline-none focus:border-violet-500/50"
                          data-testid="sb-output-name"
                        />
                      </div>
                      <button onClick={handleGenerateSpec} disabled={generating || selectedItems.length === 0}
                        className="w-full py-3 bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white font-mono text-sm
                          uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                        data-testid="sb-generate-btn"
                      >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {generating ? "Generating..." : "Generate & Download"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ADMIN TAB */}
        {activeTab === "admin" && (
          <div className="space-y-5" data-testid="sb-admin-section">
            {/* Add Section */}
            <div className="bg-white/[0.03] border border-white/10 p-5">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-400" /> Add Section
              </h3>
              <div className="flex gap-3">
                <input type="text" value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSection()}
                  placeholder="Section name"
                  className="flex-1 bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200
                    focus:outline-none focus:border-violet-500/50 placeholder-gray-600"
                  data-testid="sb-new-section-input"
                />
                <button onClick={handleAddSection}
                  className="bg-violet-600 hover:bg-violet-500 text-white px-5 py-2 font-mono text-sm"
                  data-testid="sb-add-section-btn"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Sections List */}
            <div className="bg-white/[0.03] border border-white/10 p-5">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-blue-400" /> Sections
              </h3>
              <div className="space-y-1.5">
                {sections.map(s => (
                  <div key={s.id} className="flex items-center justify-between px-3 py-2 border border-white/5" data-testid={`sb-admin-section-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono text-gray-200">{s.name}</span>
                      <span className="text-xs text-gray-600 font-mono">({getItemsBySection(s.id).length} items)</span>
                    </div>
                    {s.id !== "default" && (
                      <button onClick={() => handleDeleteSection(s.id)}
                        className="text-gray-600 hover:text-red-400 transition-colors"
                        data-testid={`sb-delete-section-${s.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Upload */}
            <div className="bg-white/[0.03] border border-white/10 p-5">
              <h3 className="text-sm font-mono font-semibold text-gray-200 mb-3 flex items-center gap-2">
                <Upload className="w-4 h-4 text-amber-400" /> Upload PPTX Files
              </h3>
              <div className="mb-3">
                <label className="text-xs text-gray-500 font-mono block mb-1">Target Section</label>
                <select value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 px-3 py-2 text-sm font-mono text-gray-200
                    focus:outline-none focus:border-violet-500/50"
                  data-testid="sb-section-select"
                >
                  {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <input ref={fileInputRef} type="file" accept=".pptx" multiple className="hidden"
                onChange={handleFileSelect} data-testid="sb-file-input" />
              <div onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/10 hover:border-violet-500/30 p-8 text-center cursor-pointer transition-colors"
                data-testid="sb-upload-zone"
              >
                <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-400 font-mono"><span className="text-violet-400">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-gray-600 font-mono mt-1">PPTX files only</p>
              </div>
              {uploadFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadFiles.map(file => (
                    <div key={file.name} className="flex items-center gap-3 border border-white/5 px-3 py-2">
                      <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span className="text-xs text-gray-400 font-mono flex-shrink-0 truncate max-w-[120px]">{file.name}</span>
                      <input type="text" placeholder="Label" value={uploadLabels[file.name] || ''}
                        onChange={(e) => setUploadLabels({ ...uploadLabels, [file.name]: e.target.value })}
                        className="flex-1 bg-white/5 border border-white/10 px-2 py-1 text-xs font-mono text-gray-200
                          focus:outline-none focus:border-violet-500/50"
                        data-testid={`sb-label-${file.name}`}
                      />
                      <button onClick={() => handleRemoveFile(file.name)} className="text-gray-600 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button onClick={handleUpload} disabled={uploading}
                    className="w-full py-2.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-mono text-sm
                      flex items-center justify-center gap-2 mt-3"
                    data-testid="sb-upload-btn"
                  >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading ? "Uploading..." : `Upload ${uploadFiles.length} File(s)`}
                  </button>
                </div>
              )}
            </div>

            {/* Library Items */}
            {items.length > 0 && (
              <div className="bg-white/[0.03] border border-white/10 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-mono font-semibold text-gray-200 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-400" /> Library Items
                  </h3>
                  {deleteItems.size > 0 && (
                    <button onClick={handleDeleteSelected}
                      className="bg-red-600 hover:bg-red-500 text-white px-4 py-1.5 font-mono text-xs flex items-center gap-1.5"
                      data-testid="sb-delete-selected-btn"
                    >
                      <Trash2 className="w-3 h-3" /> Delete {deleteItems.size}
                    </button>
                  )}
                </div>
                {sections.map(section => {
                  const sItems = getItemsBySection(section.id);
                  if (sItems.length === 0) return null;
                  return (
                    <div key={section.id} className="mb-4">
                      <div className="text-xs text-gray-500 font-mono mb-2">{section.name} — {sItems.length} file(s)</div>
                      <div className="space-y-1">
                        {sItems.map(item => (
                          <div key={item.id}
                            className={`flex items-center gap-3 px-3 py-2 border transition-colors cursor-pointer
                              ${deleteItems.has(item.id) ? "border-red-500/50 bg-red-500/10" : "border-white/5 hover:border-white/10"}`}
                            onClick={() => toggleDeleteItem(item.id)}
                          >
                            <input type="checkbox" checked={deleteItems.has(item.id)} readOnly className="accent-red-500 pointer-events-none"
                              data-testid={`sb-delete-cb-${item.id}`} />
                            <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-mono text-gray-200 truncate">{item.label}</div>
                              <div className="text-xs text-gray-600 truncate">{item.original_name}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
